import React, { useMemo, useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Button, Input, Popover } from 'antd';

export type AnnotationDropdownOption = {
  id?: string | number;
  code: string;
  description: string;
};

interface AnnotationFilterDropdownProps {
  value: string;
  placeholder: string;
  options?: AnnotationDropdownOption[];
  defaultValue: string;
  onChange: (value: string) => void;
  className?: string;
  compact?: boolean;
}

const AnnotationFilterDropdown: React.FC<AnnotationFilterDropdownProps> = ({
  value,
  placeholder,
  options = [],
  defaultValue,
  onChange,
  className = '',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');

  const filteredOptions = useMemo(() => {
    const normalizedFilter = filterText.trim().toLowerCase();

    if (!normalizedFilter) {
      return options;
    }

    return options.filter(option => {
      const code = option.code?.toLowerCase() ?? '';
      const description = option.description?.toLowerCase() ?? '';
      return code.includes(normalizedFilter) || description.includes(normalizedFilter);
    });
  }, [filterText, options]);

  const label = useMemo(() => {
    if (value === defaultValue) {
      return placeholder;
    }

    return options.find(option => option.code === value)?.description ?? placeholder;
  }, [defaultValue, options, placeholder, value]);

  const closeDropdown = () => {
    setOpen(false);
    setFilterText('');
  };

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setFilterText('');
        }
      }}
      content={
        <div className="rounded-lg bg-white p-2" style={{ width: compact ? 'clamp(160px, 34vw, 220px)' : 'clamp(180px, 42vw, 260px)' }}>
          <Input
            size="small"
            placeholder={`Filter ${placeholder.toLowerCase()}`}
            value={filterText}
            onChange={event => setFilterText(event.target.value)}
            className="mb-2"
          />
          <div className="max-h-56 overflow-y-auto">
            <button
              type="button"
              className="mb-1 flex w-full items-center rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-500 hover:bg-slate-100"
              onClick={() => {
                onChange(defaultValue);
                closeDropdown();
              }}>
              {placeholder}
            </button>
            {filteredOptions.map(option => (
              <button
                key={option.id ?? option.code}
                type="button"
                className="mb-1 flex w-full items-center rounded-md px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                onClick={() => {
                  onChange(option.code);
                  closeDropdown();
                }}>
                {option.description}
              </button>
            ))}
            {filteredOptions.length === 0 && <div className="px-2 py-2 text-xs text-slate-400">No matches found</div>}
          </div>
        </div>
      }>
      <Button
        className={`!flex !min-w-0 !items-center !justify-between !rounded-[7px] !border !border-[#f0d6d6] !bg-white !font-semibold !text-slate-500 !shadow-none hover:!border-[#f0d6d6] hover:!bg-white hover:!text-slate-500 ${
          compact ? '!h-[26px] !px-1.5 !text-[10px]' : '!h-[30px] !px-2 !text-[11px]'
        } ${className}`}
        style={{ width: compact ? 'clamp(70px, 7vw, 94px)' : 'clamp(82px, 18vw, 132px)' }}>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
        <DownOutlined className={`${compact ? 'text-[10px]' : 'text-[11px]'} ml-1 shrink-0 text-[#7a7a7a]`} />
      </Button>
    </Popover>
  );
};

export default AnnotationFilterDropdown;
